/// <amd-module name="Analytics/Shared/PowerbiReport/PowerbiReport" />
import { AadComponent, IAadTokenResponse } from "Analytics/Shared/AadTokenGenerator/AadTokenGenerator";
import * as Powerbi from "Analytics/Shared/PowerbiClient/Contracts/Powerbi";
import * as Resources from "Analytics/Shared/PowerbiReport/Resources";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as pbi from "powerbi-client";
import * as React from "react";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { traceException, traceError } from "VSS/Platform/Trace";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { format } from "VSS/Core/Util/String";

export interface IPowerbiReportProps {
    getReportDetails: (token: string) => Promise<Powerbi.PowerBiReportDetails>;
    accessToken?: string;
    forwardLinkOnError?: string;
    setDefaultSlicerState?: boolean;
}

export interface IPowerbiReportState {
    accessToken: string;
    reportDetails: Powerbi.PowerBiReportDetails;
    errorText: string;
}

export class PowerbiReport extends VssComponent<IPowerbiReportProps, IPowerbiReportState> {
    private _isMounted: boolean;

    constructor(props: IPowerbiReportProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {} as IPowerbiReportState;

        if (props.accessToken) {
            this.state = { accessToken: props.accessToken } as IPowerbiReportState;
            this._setToken({ token: props.accessToken });
        }
    }

    public render() {
        if (this.state.errorText) {
            return (
                <AnalyticsUnavailableMessage
                    suggestion={this.state.errorText}
                    linkUrl={this.props.forwardLinkOnError}
                    linkText={Resources.PowerBiReportHelpText}
                />
            );
        }

        return (
            <>
                {!this._canRenderPowerbiReport() && <Spinner className="powerbi-spinner flex flex-grow" size={SpinnerSize.large} />}
                {!this.state.accessToken && <AadComponent callback={this._setToken} />}
                <div className="powerbi-report-container flex-grow" />
            </>
        );
    }

    public async componentDidMount() {
        this._isMounted = true;
        this._embedPowerbiReport();
    }

    private _setToken = async (tokenResponse: IAadTokenResponse): Promise<void> => {
        if (tokenResponse.errorText) {
            this.setState({
                errorText: Resources.PowerbiReportErrorText.concat(tokenResponse.errorText).concat(Resources.PowerBiReportAuthErrorInfo)
            });
        }
        if (!tokenResponse.token) {
            return;
        }

        try {
            const report = await this.props.getReportDetails(tokenResponse.token);
            this.setState({ reportDetails: report, accessToken: tokenResponse.token }, () => {
                this._embedPowerbiReport();
            });
        } catch (error) {
            this.setState({
                errorText: Resources.PowerbiReportErrorText.concat(error).concat(Resources.PowerBiReportDetailsErrorInfo)
            });
        }
    };

    private _embedPowerbiReport() {
        if (!this._isMounted || !this._canRenderPowerbiReport()) {
            return;
        }

        const config: any = {
            type: "report",
            tokenType: 0,
            accessToken: this.state.accessToken,
            embedUrl: this.state.reportDetails.embedUrl,
            reportId: this.state.reportDetails.id,
            permissions: 7,
            settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: true
            }
        };
        const reportContainer: any = document.getElementsByClassName("powerbi-report-container")[0];
        const powerbiService = new pbi.service.Service(pbi.factories.hpmFactory, pbi.factories.wpmpFactory, pbi.factories.routerFactory);
        var report = powerbiService.embed(reportContainer, config) as pbi.Report;
        this._setDefaultSlicerState(report);
    }

    private _canRenderPowerbiReport() {
        return this.state.reportDetails && this.state.accessToken;
    }

    private _setDefaultSlicerState(report: pbi.Report) {
        if (this.props.setDefaultSlicerState) {
            report.on("pageChanged", () => {
                this._onPageChanged(report)
            })
        }
    }

    private async _onPageChanged(report: pbi.Report) {
        let pages = await report.getPages().catch(e => {
            traceError(this.context.pageContext, this._telemetryArea, this._telemetryFeature, format("Error while get pages: {0}", e));
        });
        if(!pages){
            return;
        }
        let activePage = pages.filter((page) => {
            return page.isActive
        })[0];

        if (this._pagesInitialized.indexOf(activePage.name) >= 0) {
            return;
        }

        let visuals: pbi.VisualDescriptor[] = await activePage.getVisuals()
        let filteredVisuals = this._getSlicerVisuals(visuals);

        await this._exportDataAsync(filteredVisuals);
        this._pagesInitialized.push(activePage.name);
    }

    private _getSlicerVisuals(visuals: pbi.VisualDescriptor[]): pbi.VisualDescriptor[] {
        // Retrieve the slicers.
        var slicerVisuals = visuals.filter((visual) => {
            return visual.type == "slicer";
        });

        return slicerVisuals;
    }

    private async _exportDataAsync(visuals: pbi.VisualDescriptor[]) {
        for (const index in visuals) {
            // wait for the promise to resolve before advancing the next call as export data calls cannot be made parallely.
            await this._exportSlicerData(visuals[index]);
        }

    }

    private async _exportSlicerData(visual: pbi.VisualDescriptor) {
        let exportedResult: any = await visual.exportData().catch(e => {
            traceError(this.context.pageContext, this._telemetryArea, this._telemetryFeature, format("Error while export visual: {0}", e));
        });
        let result = exportedResult as pbi.models.IExportDataResult;
        if (!result || !result.data) {
            return;
        }
        let dataList = this._getExportDataList(result.data);
        let filterValue = this._getDefaultValue(dataList);
        if (filterValue === "") {
            return;
        }

        let currentState:pbi.models.ISlicerState | void = await visual.getSlicerState().catch(e => {
            traceError(this.context.pageContext, this._telemetryArea, this._telemetryFeature, format("Error while get slicer state: {0}", e));
        });
        if(currentState){
            let currentSlicerSate:string = this._getCurrentSlicerState(currentState);
            // If currentState is not ALL and if the current state is not present in the valid slicer states then change the slicer state.
            if(currentSlicerSate !== "" && dataList.indexOf(currentSlicerSate) < 0) {
                visual.setSlicerState({ filters: [this._initializeFilter(filterValue, currentState)] });
            }
        }
    }

    private _getCurrentSlicerState(currentState: pbi.models.ISlicerState) : string{
        let filter: any = currentState.filters.find(filter => filter.filterType === pbi.models.FilterType.Basic);
        if(filter){
            return filter.values[0];
        }

        return "";
    }

    private _getDefaultValue(filterStrings: string[]): string {
        // Return the last element as it is latest.
        if (filterStrings && filterStrings.length > 1) {
            return filterStrings[filterStrings.length - 1];
        }

        return "";
    }

    private _getExportDataList(csvString: string): string[] {
        // Parse and return array filtering empty elements.
        return csvString.toString().split(this._csvDelimeter).filter(Boolean);
    }

    private _initializeFilter(filterValue: string, currentState: pbi.models.ISlicerState) {
        const basicFilter: pbi.models.IBasicFilter = {
            $schema: "http://powerbi.com/product/schema#basic",
            target: currentState.targets ? currentState.targets[0] : null,
            operator: "In",
            values: [filterValue],
            filterType: pbi.models.FilterType.Basic
        } as pbi.models.IBasicFilter;

        return basicFilter
    }

    private _pagesInitialized: string[] = [];
    private _csvDelimeter: string = "\r\n";

    private _telemetryArea = "Analytics";
    private _telemetryFeature = "PowerBi";
    private _errorMessage = "Error while trying to set default slicer values: "
}

Components.add("powerbiReport", PowerbiReport);
